import { Link } from "react-router-dom";
import { ArrowRight, ShieldCheck, Lock, BadgeCheck, MapPin, Calculator, Sparkles, FileText, Home as HomeIcon, RefreshCw, PiggyBank, ChevronRight, Landmark } from "lucide-react";
import ComplianceStrip from "../components/ComplianceStrip";
import Seo from "../components/Seo";
import { CashbackBanner } from "../components/CashbackPrompt";
import { cashbackPromotionEnabled } from "../config/promotions";
import { useEffect, useMemo, useState } from "react";
import { monthlyRepayment, fmtGBP } from "../lib/calc";
import { track } from "../lib/api";
import { Slider } from "../components/ui/slider";
import { useEditableNumber } from "../hooks/useEditableNumber";

const TrustItem = ({ icon: Icon, children }) => (
  <div className="flex items-center gap-2 text-sm text-white/90">
    <span className="w-7 h-7 rounded-full bg-white/15 flex items-center justify-center"><Icon className="w-4 h-4" /></span>
    {children}
  </div>
);

const Step = ({ n, title, body }) => (
  <div className="mp-card p-7 mp-card-hover">
    <div className="w-9 h-9 rounded-full bg-[#0F766E]/10 text-[#0F766E] flex items-center justify-center font-sora font-semibold">{n}</div>
    <h3 className="mt-4 font-sora text-xl font-semibold text-[#0B1220]">{title}</h3>
    <p className="mt-2 text-sm leading-relaxed text-[#475569]">{body}</p>
  </div>
);

const MiniCalc = () => {
  const [price, setPrice] = useState(300000);
  const [deposit, setDeposit] = useState(30000);
  const [term, setTerm] = useState(25);
  const [rate, setRate] = useState(4.75);
  const priceInput = useEditableNumber(price, setPrice, { allowEmpty: true });
  const depositInput = useEditableNumber(deposit, setDeposit, { allowEmpty: true });
  const loan = Math.max((Number(price) || 0) - (Number(deposit) || 0), 0);
  const m = useMemo(() => monthlyRepayment(loan, rate, term), [loan, rate, term]);
  const termValue = useMemo(() => [term], [term]);
  const rateValue = useMemo(() => [rate], [rate]);
  return (
    <div className="mp-card p-6 sm:p-7" data-testid="home-mini-calc">
      <div className="flex items-center justify-between">
        <div>
          <div className="text-xs uppercase tracking-widest text-[#0F766E]">Quick estimate</div>
          <div className="font-sora text-xl font-semibold text-[#0B1220]">Mini mortgage calculator</div>
        </div>
        <Calculator className="w-5 h-5 text-[#0F766E]" />
      </div>
      <div className="grid grid-cols-2 gap-3 mt-5">
        <label className="block">
          <span className="mp-label">Property price</span>
          <input type="number" className="mp-input" value={priceInput.displayValue} {...priceInput.inputProps} data-testid="mini-calc-price" />
        </label>
        <label className="block">
          <span className="mp-label">Deposit</span>
          <input type="number" className="mp-input" value={depositInput.displayValue} {...depositInput.inputProps} data-testid="mini-calc-deposit" />
        </label>
        <div className="col-span-2">
          <div className="flex items-center justify-between mb-2">
            <span className="mp-label !mb-0">Term</span>
            <span className="font-sora text-sm font-semibold text-[#0B5F58] tabular-nums" data-testid="mini-calc-term-value">{term} {term === 1 ? "year" : "years"}</span>
          </div>
          <div className="rounded-xl border border-[#DCE7E3] bg-white px-4 py-3">
            <Slider min={5} max={40} step={1} value={termValue} onValueChange={(v) => setTerm(v[0])} data-testid="mini-calc-term" />
            <div className="mt-1.5 flex justify-between text-[11px] text-[#475569] tabular-nums"><span>5 yrs</span><span>40 yrs</span></div>
          </div>
        </div>
        <div className="col-span-2">
          <div className="flex items-center justify-between mb-2">
            <span className="mp-label !mb-0">Interest rate</span>
            <span className="font-sora text-sm font-semibold text-[#0B5F58] tabular-nums" data-testid="mini-calc-rate-value">{rate.toFixed(2)}%</span>
          </div>
          <div className="rounded-xl border border-[#DCE7E3] bg-white px-4 py-3">
            <Slider min={1} max={10} step={0.01} value={rateValue} onValueChange={(v) => setRate(v[0])} data-testid="mini-calc-rate" />
            <div className="mt-1.5 flex justify-between text-[11px] text-[#475569] tabular-nums"><span>1%</span><span>10%</span></div>
          </div>
        </div>
      </div>
      <div className="mt-5 rounded-xl bg-[#F7FAF9] border border-[#DCE7E3] p-4 flex items-center justify-between">
        <div>
          <div className="text-xs text-[#475569]">Estimated monthly repayment</div>
          <div className="font-sora text-2xl font-bold text-[#0B5F58]" data-testid="mini-calc-result">{fmtGBP(m)}</div>
        </div>
        <Link to="/calculator" className="mp-btn-secondary text-sm" data-testid="mini-calc-open-full">
          Open full calculator <ArrowRight className="w-4 h-4" />
        </Link>
      </div>
      <p className="mt-3 text-xs text-[#475569]">Figures are estimates only and do not represent a mortgage offer.</p>
    </div>
  );
};

const questions = [
  { t: "How much could I borrow?", b: "Most UK lenders use income multiples between roughly 4× and 4.75×, adjusted for commitments." },
  { t: "How much deposit do I need?", b: "5% is the typical entry point; 10–15% usually opens more competitive products." },
  { t: "How do lenders assess income?", b: "Salary, regular bonuses, self-employed profits, and benefits all factor in — with documentation." },
  { t: "What affects affordability?", b: "Credit commitments, dependants, monthly outgoings and stress-tested rates all play a part." },
  { t: "Should I remortgage now?", b: "It depends on your current product end date, equity, and any early repayment charges." },
  { t: "What happens after an AIP?", b: "Agreement in Principle is an indication, not a final offer. The full underwriting comes later." },
];

const Home = () => {
  useEffect(() => { track("home_view"); }, []);
  return (
    <>
      <Seo
        title="MortgagePath | Free UK Mortgage Snapshot & Educational Guidance"
        description="Understand how UK mortgages are typically assessed with MortgagePath's free educational snapshot and calculator. No credit check, no obligation and no regulated advice."
        path="/"
      />
      {/* HERO */}
      <section className="relative overflow-hidden hero-noise" style={{ background: "linear-gradient(135deg,#0B5F58 0%, #0F766E 50%, #10B981 100%)" }}>
        <div className="mp-container relative py-20 md:py-28 grid lg:grid-cols-12 gap-10 items-center">
          <div className="lg:col-span-7 text-white mp-rise mp-rise-1">
            <div className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.18em] bg-white/10 border border-white/20 rounded-full px-3 py-1.5">
              <Sparkles className="w-3.5 h-3.5" /> UK mortgage education
            </div>
            <h1 className="font-sora mt-5 text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight leading-[1.05]">
              Understand UK mortgages — <span className="text-[#A7F3D0]">before</span> you speak to an adviser
            </h1>
            <p className="mt-6 text-base sm:text-lg text-white/85 leading-relaxed max-w-2xl">
              Clear, educational guidance to help you understand how mortgages are typically assessed —
              with no pressure and no obligation.
            </p>
            <div className="mt-8 flex flex-wrap gap-3">
              <Link to="/snapshot" className="inline-flex items-center gap-2 bg-white text-[#0B5F58] hover:bg-[#F7FAF9] font-semibold rounded-full px-6 py-3 transition-colors" data-testid="hero-cta-snapshot" onClick={() => track("snapshot_start_clicked", { cta_location: "homepage_hero", cta_text: "Start your free mortgage snapshot" })}>
                Start your free mortgage snapshot <ArrowRight className="w-4 h-4" />
              </Link>
              <Link to="/calculator" className="inline-flex items-center gap-2 bg-transparent text-white border border-white/50 hover:bg-white/10 hover:border-white font-semibold rounded-full px-6 py-3 transition-colors" data-testid="hero-cta-calculator" onClick={() => track("cta_clicked", { cta_location: "homepage_hero", cta_text: "Use the mortgage calculator", destination: "/calculator" })}>
                Use the mortgage calculator
              </Link>
            </div>
            <p className="mt-4 text-xs text-white/70">Educational guidance only. No credit checks. No obligation.</p>
            <div className="mt-9 grid grid-cols-2 sm:flex sm:flex-wrap items-center gap-x-6 gap-y-3 pt-6 border-t border-white/15">
              <TrustItem icon={Lock}>No credit score impact</TrustItem>
              <span className="hidden sm:inline w-px h-4 bg-white/20" aria-hidden="true" />
              <TrustItem icon={ShieldCheck}>Free &amp; confidential</TrustItem>
              <span className="hidden sm:inline w-px h-4 bg-white/20" aria-hidden="true" />
              <TrustItem icon={BadgeCheck}>Educational guidance only</TrustItem>
              <span className="hidden sm:inline w-px h-4 bg-white/20" aria-hidden="true" />
              <TrustItem icon={MapPin}>UK mortgage-focused</TrustItem>
            </div>
          </div>
          <div className="lg:col-span-5 mp-rise mp-rise-2">
            <MiniCalc />
          </div>
        </div>
      </section>

      {/* £100 HOMEBUYER CASHBACK — compact promo band, directly below hero */}
      {cashbackPromotionEnabled && <CashbackBanner source="homepage" />}

      {/* FEATURE CARDS */}
      <section className="mp-container py-20">
        <div className="grid md:grid-cols-3 gap-6">
          <div className="mp-card p-8 mp-card-hover">
            <div className="w-11 h-11 rounded-xl bg-[#0F766E]/10 flex items-center justify-center"><FileText className="w-5 h-5 text-[#0F766E]" /></div>
            <h2 className="font-sora mt-5 text-2xl font-semibold text-[#0B1220]">Get your free Mortgage Snapshot</h2>
            <p className="mt-3 text-[#475569] leading-relaxed">
              Answer a few simple questions and get a clear educational summary of how your mortgage position may be viewed.
            </p>
            <Link to="/snapshot" className="mp-btn-primary mt-6" data-testid="feature-card-snapshot" onClick={() => track("snapshot_start_clicked", { cta_location: "homepage_feature_card", cta_text: "Start snapshot" })}>
              Start snapshot <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
          <div className="mp-card p-8 mp-card-hover">
            <div className="w-11 h-11 rounded-xl bg-[#10B981]/10 flex items-center justify-center"><Calculator className="w-5 h-5 text-[#10B981]" /></div>
            <h2 className="font-sora mt-5 text-2xl font-semibold text-[#0B1220]">Estimate your monthly payments</h2>
            <p className="mt-3 text-[#475569] leading-relaxed">
              Use our simple calculator to estimate monthly repayments, total interest, and how overpayments could affect your mortgage.
            </p>
            <Link to="/calculator" className="mp-btn-primary mt-6" data-testid="feature-card-calculator" onClick={() => track("cta_clicked", { cta_location: "homepage_feature_card", cta_text: "Open calculator", destination: "/calculator" })}>
              Open calculator <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
          <div className="mp-card p-8 mp-card-hover">
            <div className="w-11 h-11 rounded-xl bg-[#0F766E]/10 flex items-center justify-center"><Landmark className="w-5 h-5 text-[#0F766E]" /></div>
            <h2 className="font-sora mt-5 text-2xl font-semibold text-[#0B1220]">Stamp Duty Calculator</h2>
            <p className="mt-3 text-[#475569] leading-relaxed">
              Estimate SDLT, LTT or LBTT on a UK property purchase.
            </p>
            <Link to="/stamp-duty-calculator" className="mp-btn-primary mt-6" data-testid="feature-card-stamp-duty" onClick={() => track("cta_clicked", { cta_location: "homepage_feature_card", cta_text: "Calculate property tax", destination: "/stamp-duty-calculator" })}>
              Calculate property tax <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
      </section>

      {/* HOW IT WORKS */}
      <section className="bg-white border-y border-[#DCE7E3]">
        <div className="mp-container py-20">
          <div className="max-w-2xl">
            <div className="text-xs uppercase tracking-widest text-[#0F766E]">How MortgagePath works</div>
            <h2 className="font-sora mt-3 text-3xl sm:text-4xl font-semibold text-[#0B1220] tracking-tight">Three simple steps to clearer numbers</h2>
          </div>
          <div className="grid md:grid-cols-3 gap-6 mt-10">
            <Step n="1" title="Tell us your situation" body="First-time buyer or remortgage, property value, deposit or equity, income and monthly commitments." />
            <Step n="2" title="Get a clear snapshot" body="We explain the key figures in plain English — loan size, LTV, monthly repayment and affordability bands." />
            <Step n="3" title="Choose your next step" body="Read a guide, use our free tools, or access professional support when you're ready." />
          </div>
        </div>
      </section>

      {/* QUESTIONS GRID */}
      <section className="mp-container py-20">
        <div className="flex items-end justify-between gap-6 flex-wrap">
          <div className="max-w-2xl">
            <div className="text-xs uppercase tracking-widest text-[#0F766E]">Built around real UK mortgage questions</div>
            <h2 className="font-sora mt-3 text-3xl sm:text-4xl font-semibold text-[#0B1220] tracking-tight">
              Plain-English answers to the questions buyers actually ask
            </h2>
          </div>
          <Link to="/faqs" className="mp-btn-secondary" data-testid="home-see-all-faqs">See all FAQs <ChevronRight className="w-4 h-4" /></Link>
        </div>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5 mt-10">
          {questions.map((q, i) => (
            <div key={q.t} className="mp-card p-6 mp-card-hover">
              <div className="text-[11px] uppercase tracking-widest text-[#10B981]">Topic {i + 1}</div>
              <h3 className="font-sora mt-2 text-lg font-semibold text-[#0B1220]">{q.t}</h3>
              <p className="mt-2 text-sm text-[#475569] leading-relaxed">{q.b}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Journey shortcuts */}
      <section className="mp-container pb-20">
        <div className="grid md:grid-cols-3 gap-5">
          <Link to="/first-time-buyer-guide" className="mp-card p-7 mp-card-hover" data-testid="home-link-ftb">
            <HomeIcon className="w-6 h-6 text-[#0F766E]" />
            <h3 className="font-sora mt-3 text-lg font-semibold">First-time buyer</h3>
            <p className="text-sm text-[#475569] mt-1">Deposit, AIP, affordability and the full buying journey explained.</p>
          </Link>
          <Link to="/remortgage-guide" className="mp-card p-7 mp-card-hover" data-testid="home-link-remo">
            <RefreshCw className="w-6 h-6 text-[#0F766E]" />
            <h3 className="font-sora mt-3 text-lg font-semibold">Remortgaging</h3>
            <p className="text-sm text-[#475569] mt-1">When to start, product transfer vs remortgage, and ERCs.</p>
          </Link>
          <Link to="/calculator" className="mp-card p-7 mp-card-hover" data-testid="home-link-overpayment">
            <PiggyBank className="w-6 h-6 text-[#0F766E]" />
            <h3 className="font-sora mt-3 text-lg font-semibold">Overpayments</h3>
            <p className="text-sm text-[#475569] mt-1">See how regular overpayments shorten your term and total interest.</p>
          </Link>
        </div>
      </section>

      <section className="mp-container pb-24">
        <ComplianceStrip />
      </section>
    </>
  );
};

export default Home;
